import BaseController from "./BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";

// sap.f.cards.NumericHeader.state używa typu sap.m.ValueColor (NIE sap.ui.core.ValueState).
// Dozwolone wartości: None / Good / Error / Critical / Neutral.
interface KpiCard {
	key: string;
	title: string;
	subtitle: string;
	// String only — sap.f.cards.NumericHeader.number jest typu string. UI5 ManagedObject
	// próbuje konwertować ale czasem strict validators rzucają błąd przy raw Number.
	value: string;
	state: "None" | "Good" | "Error" | "Critical" | "Neutral";
}

interface KpiQuery {
	key: string;
	titleKey: string;
	subtitleKey: string;
	state: KpiCard["state"];
	entitySet: string;
	staticFilter?: string;
	dynamicFilter?: () => string;
}

interface QuickActionItem {
	key: string;
	title: string;
	subtitle: string;
	icon: string;
	// String — Header.statusText jest typu string.
	count: string;
	route: string;
}

interface QuickActionDef {
	key: string;
	titleKey: string;
	subtitleKey: string;
	icon: string;
	route: string;
	entitySet: string;
	filter?: string;
}

interface ExpiringLicense {
	Id: string;
	ProductName: string;
	OrganizationName: string;
	ValidUntil: string;
	ValidUntilFormatted: string;
	daysLeft: number;
	daysLeftText: string;
	daysState: "Warning" | "Error";
}

interface RecentActivityEntry {
	Id: string;
	OccurredAt: string;
	Action: string;
	ChangeType: string;
	text: string;
}

const ROLE_ADMIN = ["platform-admin", "platform_admin"];
const ROLE_PARTNER = ["partner-admin", "partner-user"];
const ROLE_ENDCUSTOMER = ["endcustomer-admin", "endcustomer-user"];

const KPIS: readonly KpiQuery[] = [
	{
		key: "deployments",
		titleKey: "dashboard.kpi.deployments.title",
		subtitleKey: "dashboard.kpi.deployments.subtitle",
		state: "Good",
		entitySet: "Deployments",
		staticFilter: "Status eq 'Active'"
	},
	{
		key: "entitlements",
		titleKey: "dashboard.kpi.entitlements.title",
		subtitleKey: "dashboard.kpi.entitlements.subtitle",
		state: "Neutral",
		entitySet: "Entitlements",
		staticFilter: "Status eq 'Active'"
	},
	{
		key: "pendingOrders",
		titleKey: "dashboard.kpi.pendingOrders.title",
		subtitleKey: "dashboard.kpi.pendingOrders.subtitle",
		state: "Critical",
		entitySet: "Orders",
		staticFilter: "Status eq 'Placed'"
	},
	{
		key: "expiringSoon",
		titleKey: "dashboard.kpi.expiringSoon.title",
		subtitleKey: "dashboard.kpi.expiringSoon.subtitle",
		state: "Error",
		entitySet: "Licenses",
		dynamicFilter: () => expiringLicensesFilter(new Date())
	}
];

const EXPIRING_WINDOW_DAYS = 30;
const MS_PER_DAY = 86_400_000;

/** YYYY-MM-DD of the browser's local calendar date, not of the UTC instant. */
function localIsoDate(d: Date): string {
	const month = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${d.getFullYear()}-${month}-${day}`;
}

function startOfLocalDay(d: Date): Date {
	return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Local midnight of a YYYY-MM-DD date; new Date("YYYY-MM-DD") would give UTC midnight instead. */
function parseLocalDate(isoDate: string): Date {
	const [year, month, day] = isoDate.slice(0, 10).split("-").map(Number);
	return new Date(year, month - 1, day);
}

/** The single "expiring" condition shared by the KPI tile and the table below it. */
function expiringLicensesFilter(today: Date): string {
	const from = startOfLocalDay(today);
	const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + EXPIRING_WINDOW_DAYS);
	return `Status eq 'Active' and ValidUntil ge ${localIsoDate(from)} and ValidUntil lt ${localIsoDate(to)}`;
}

/**
 * The audit actions Recent Activity shows: business events only, never commands, data mutations or
 * License Runtime traffic. Each one has a dashboard.recentActivity.event.<event name> text.
 */
const RECENT_ACTIVITY_ACTIONS: readonly string[] = [
	"Order.OrderPlaced",
	"Order.OrderApproved",
	"Order.OrderPaid",
	"License.LicenseCreated",
	"License.LicenseUnbound",
	"LicenseAssignment.UserAssignedToLicense",
	"User.UserInvited",
	"User.UserActivated",
	"Deployment.DeploymentEnrolled",
	"Organization.OrganizationRegistered"
];

function recentActivityUrl(): string {
	const actions = RECENT_ACTIVITY_ACTIONS.map(a => `'${a}'`).join(",");
	const filter = encodeURIComponent(`ChangeType eq 'DomainEvent' and Action in (${actions})`);
	const orderBy = encodeURIComponent("OccurredAt desc");
	return `/odata/v4/AuditEntries?$top=6&$orderby=${orderBy}&$select=Id,OccurredAt,Action,ChangeType,ActorEmail&$filter=${filter}`;
}

/** Rounded rather than ceiled: a day across a DST change lasts 23 or 25 hours. */
function daysUntil(today: Date, isoDate: string): number {
	return Math.round((parseLocalDate(isoDate).getTime() - startOfLocalDay(today).getTime()) / MS_PER_DAY);
}

// Quick Actions per rola — admin widzi system-wide management, partner widzi codzienne
// zadania (dodawanie klientów, zamówienia, licencje).
const QUICK_ACTIONS_ADMIN: readonly QuickActionDef[] = [
	{ key: "partners",    titleKey: "dashboard.qa.partners.title",    subtitleKey: "dashboard.qa.partners.subtitle",    icon: "sap-icon://add-employee", route: "partners",    entitySet: "Organizations", filter: "Type eq 'Partner'" },
	{ key: "customers",   titleKey: "dashboard.qa.customers.title",   subtitleKey: "dashboard.qa.customers.subtitle",   icon: "sap-icon://customer",     route: "customers",   entitySet: "Organizations", filter: "Type eq 'EndCustomer'" },
	{ key: "products",    titleKey: "dashboard.qa.products.title",    subtitleKey: "dashboard.qa.products.subtitle",    icon: "sap-icon://product",      route: "products",    entitySet: "Products" },
	{ key: "orders",      titleKey: "dashboard.qa.orders.title",      subtitleKey: "dashboard.qa.orders.subtitle",      icon: "sap-icon://sales-order",  route: "orders",      entitySet: "Orders" },
	{ key: "deployments", titleKey: "dashboard.qa.deployments.title", subtitleKey: "dashboard.qa.deployments.subtitle", icon: "sap-icon://it-instance",  route: "deployments", entitySet: "Deployments" },
	{ key: "licenses",    titleKey: "dashboard.qa.licenses.title",    subtitleKey: "dashboard.qa.licenses.subtitle",    icon: "sap-icon://document",     route: "licenses",    entitySet: "Licenses" }
];

const QUICK_ACTIONS_PARTNER: readonly QuickActionDef[] = [
	{ key: "myCustomers",  titleKey: "dashboard.qa.myCustomers.title",  subtitleKey: "dashboard.qa.myCustomers.subtitle",  icon: "sap-icon://customer",      route: "customers",   entitySet: "Organizations", filter: "Type eq 'EndCustomer'" },
	{ key: "newOrder",     titleKey: "dashboard.qa.newOrder.title",     subtitleKey: "dashboard.qa.newOrder.subtitle",     icon: "sap-icon://sales-order",   route: "orderNew",    entitySet: "Orders" },
	{ key: "myOrders",     titleKey: "dashboard.qa.myOrders.title",     subtitleKey: "dashboard.qa.myOrders.subtitle",     icon: "sap-icon://list",          route: "orders",      entitySet: "Orders" },
	{ key: "myLicenses",   titleKey: "dashboard.qa.myLicenses.title",   subtitleKey: "dashboard.qa.myLicenses.subtitle",   icon: "sap-icon://document",      route: "licenses",    entitySet: "Licenses",     filter: "Status eq 'Active'" },
	{ key: "myDeployments", titleKey: "dashboard.qa.myDeployments.title", subtitleKey: "dashboard.qa.myDeployments.subtitle", icon: "sap-icon://it-instance", route: "deployments", entitySet: "Deployments", filter: "Status eq 'Active'" },
	{ key: "products",     titleKey: "dashboard.qa.products.title",     subtitleKey: "dashboard.qa.products.subtitle",     icon: "sap-icon://product",       route: "products",    entitySet: "Products" }
];

/**
 * @namespace computec.b2b.portal.controller
 */
export default class Dashboard extends BaseController {
	public override onInit(): void {
		// Ustaw puste modele synchronicznie zanim XML się wyrenderuje — inaczej f:GridContainer
		// items="{kpi>/cards}" i items="{quickActions>/items}" widzą undefined model przy
		// pierwszym renderze i nie binduje się aggregation. async initialize() wypełnia je
		// po pobraniu i18n + count'ów.
		this.getView().setModel(new JSONModel({ cards: [] }), "kpi");
		this.getView().setModel(new JSONModel({ items: [] }), "quickActions");
		this.getView().setModel(new JSONModel({ entries: [] }), "recentActivity");
		this.getView().setModel(new JSONModel({ items: [], subtitle: "" }), "expiringLicenses");
		this.getView().setModel(new JSONModel({ qaGridCols: "repeat(auto-fit, minmax(14rem, 1fr))" }), "view");

		this.getRouter().getRoute("dashboard")?.attachPatternMatched(() => { this.onRouteMatched(); });
	}

	private initialized = false;

	/**
	 * The only entry point of the dashboard: the route fires on the first load as well as on every later
	 * visit, so the orders.read redirect has a single call site and runs before any request goes out.
	 */
	private onRouteMatched(): void {
		if (this.redirectWithoutOrders()) {
			return;
		}
		if (!this.initialized) {
			this.initialized = true;
			void this.initialize();
			return;
		}
		void this.refresh();
	}

	/**
	 * Every tile of the dashboard reads a feed behind orders.read or a sibling of it, so a caller
	 * without orders.read (the record-scoped CompuTec developer) would only collect 403s here. Such a
	 * caller lands on Deployments instead, before any $count leaves the browser.
	 * @returns true when the redirect fired and the caller must stop.
	 */
	private redirectWithoutOrders(): boolean {
		if (this.getOwnerComponent().getPermissions().has("orders.read")) {
			return false;
		}
		this.getRouter().navTo("deployments", {}, true);
		return true;
	}

	private quickActions: readonly QuickActionDef[] = QUICK_ACTIONS_ADMIN;

	private async initialize(): Promise<void> {
		const bundle = await this.getResourceBundle();

		// Hero — derive role display from user model roles claim.
		const userModel = this.getOwnerComponent().getModel("user") as JSONModel | undefined;
		const roles = (userModel?.getProperty("/roles") as string[] | undefined) ?? [];
		const isAdmin = roles.some(r => ROLE_ADMIN.includes(r));
		const isPartner = roles.some(r => ROLE_PARTNER.includes(r));
		if (userModel) {
			userModel.setProperty(
				"/displayRole",
				isAdmin
					? bundle.getText("dashboard.role.admin") ?? "CompuTec Admin"
					: isPartner
						? bundle.getText("dashboard.role.partner") ?? "Partner"
						: bundle.getText("dashboard.role.user") ?? "User"
			);
			userModel.setProperty("/roleState", isAdmin ? "Information" : isPartner ? "Success" : "None");
		}

		// Per-role Quick Actions: partner dostaje workflow-oriented zestaw, admin system-wide.
		// Endcustomer fall-throughem trafia do admin set, ale Partners/Customers cards są
		// wtedy odfiltrowane (customer nie zarządza innymi orgami).
		const isEndCustomer = roles.some(r => ROLE_ENDCUSTOMER.includes(r));
		this.quickActions = isPartner
			? QUICK_ACTIONS_PARTNER
			: isEndCustomer
				? QUICK_ACTIONS_ADMIN.filter(qa => qa.key !== "partners" && qa.key !== "customers")
				: QUICK_ACTIONS_ADMIN;

		// Quick Actions inner grid: all roles → 2 cols (admin/partner: 3 rows, customer: 2 rows).
		const qaGridCols = "repeat(2, 1fr)";
		(this.getView().getModel("view") as JSONModel).setProperty("/qaGridCols", qaGridCols);

		// KPI cards — start with em-dash, fill in via $count requests.
		// Aktualizujemy ISTNIEJĄCY model z onInit zamiast tworzyć nowy — wymiana modelu po
		// pierwszym renderze gubiła bindingi `cards:NumericHeader.state` na pojedynczych
		// kartach (state nie był ponownie zwalidowany typem ValueColor po re-bindingu).
		const kpiCards: KpiCard[] = KPIS.map(q => ({
			key: q.key,
			title: bundle.getText(q.titleKey) ?? q.key,
			subtitle: bundle.getText(q.subtitleKey) ?? "",
			value: "—",
			state: q.state
		}));
		(this.getView().getModel("kpi") as JSONModel).setProperty("/cards", kpiCards);

		// Quick Actions — same pattern (update existing model, don't replace).
		const quickItems: QuickActionItem[] = this.quickActions.map(qa => ({
			key: qa.key,
			title: bundle.getText(qa.titleKey) ?? qa.key,
			subtitle: bundle.getText(qa.subtitleKey) ?? "",
			icon: qa.icon,
			count: "—",
			route: qa.route
		}));
		(this.getView().getModel("quickActions") as JSONModel).setProperty("/items", quickItems);

		await this.refresh();
	}

	private async refresh(): Promise<void> {
		await Promise.all([
			this.refreshKpis(),
			this.refreshQuickActions(),
			this.refreshRecentActivity(),
			this.refreshExpiringLicenses()
		]);
	}

	private async refreshRecentActivity(): Promise<void> {
		const model = this.getView().getModel("recentActivity") as JSONModel | undefined;
		if (!model) {
			return;
		}
		// The card is hidden in the view without audit-log.read; the feed would only answer 403.
		if (!this.getOwnerComponent().getPermissions().has("audit-log.read")) {
			return;
		}
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const r = await fetcher.fetch(recentActivityUrl(), { headers: { Accept: "application/json" } });
			if (!r.ok) {
				model.setProperty("/entries", []);
				return;
			}
			const data = await r.json() as { value?: Array<Omit<RecentActivityEntry, "text">> };
			const bundle = await this.getResourceBundle();
			const entries: RecentActivityEntry[] = (Array.isArray(data.value) ? data.value : []).map(e => ({
				...e,
				text: bundle.getText(`dashboard.recentActivity.event.${e.Action.slice(e.Action.indexOf(".") + 1)}`) ?? e.Action
			}));
			model.setProperty("/entries", entries);
		} catch {
			model.setProperty("/entries", []);
		}
	}

	private async refreshKpis(): Promise<void> {
		const model = this.getView().getModel("kpi") as JSONModel | undefined;
		if (!model) {
			return;
		}
		await Promise.all(KPIS.map(async (q, idx) => {
			const filter = q.dynamicFilter ? q.dynamicFilter() : q.staticFilter;
			const value = await this.fetchCount(q.entitySet, filter);
			if (value !== null) {
				model.setProperty(`/cards/${idx}/value`, String(value));
			}
		}));
	}

	private async refreshQuickActions(): Promise<void> {
		const model = this.getView().getModel("quickActions") as JSONModel | undefined;
		if (!model) {
			return;
		}
		await Promise.all(this.quickActions.map(async (qa, idx) => {
			const value = await this.fetchCount(qa.entitySet, qa.filter);
			if (value !== null) {
				model.setProperty(`/items/${idx}/count`, String(value));
			}
		}));
	}

	private async refreshExpiringLicenses(): Promise<void> {
		const model = this.getView().getModel("expiringLicenses") as JSONModel | undefined;
		if (!model) return;
		const fetcher = this.getOwnerComponent().getAuthFetch();
		const today = new Date();
		const filter = encodeURIComponent(expiringLicensesFilter(today));
		const url = `/odata/v4/Licenses?$filter=${filter}&$select=Id,ProductName,OrganizationName,ValidUntil&$orderby=ValidUntil&$top=20&$count=true`;
		try {
			const r = await fetcher.fetch(url, { headers: { Accept: "application/json" } });
			if (!r.ok) return;
			const data = await r.json() as {
				"@odata.count"?: number;
				value: Array<{ Id: string; ProductName: string; OrganizationName: string; ValidUntil: string }>;
			};
			const bundle = await this.getResourceBundle();
			const unit = bundle.getText("dashboard.expiringLicenses.daysUnit") ?? "dni";
			const items: ExpiringLicense[] = (data.value ?? []).map(l => {
				const daysLeft = daysUntil(today, l.ValidUntil);
				return {
					...l,
					ValidUntilFormatted: l.ValidUntil.slice(0, 10),
					daysLeft,
					daysLeftText: `${daysLeft} ${unit}`,
					daysState: daysLeft <= 14 ? "Error" : "Warning"
				};
			});
			const total = data["@odata.count"] ?? items.length;
			const subtitle = items.length > 0
				? (bundle.getText("dashboard.expiringLicenses.subtitle", [String(total)]) ?? `${total}`)
				: "";
			model.setProperty("/items", items);
			model.setProperty("/subtitle", subtitle);
		} catch {
			// fail silently — widget stays empty
		}
	}

	public onRenewLicense(): void {
		this.navTo("licenses", {});
	}

	private async fetchCount(entitySet: string, filter?: string): Promise<number | null> {
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const filterPart = filter ? `?$filter=${encodeURIComponent(filter)}` : "";
			const r = await fetcher.fetch(`/odata/v4/${entitySet}/$count${filterPart}`, {
				headers: { Accept: "text/plain" }
			});
			if (!r.ok) {
				return null;
			}
			const n = Number((await r.text()).trim());
			return Number.isFinite(n) ? n : null;
		} catch {
			return null;
		}
	}

	public onQuickAction(event: { getSource(): { getBindingContext(name: string): { getProperty(path: string): unknown } | null } }): void {
		const ctx = event.getSource().getBindingContext("quickActions");
		if (!ctx) {
			return;
		}
		const route = ctx.getProperty("route") as string | undefined;
		if (route) {
			this.navTo(route, {});
		}
	}
}
